import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { ShieldCheck, RefreshCw, DatabaseZap, CloudDownload, AlertTriangle } from "lucide-react";
import { MKT, getAdminAuth } from "./AdminEmailMarketing";

const STATUS_META = [
  ["valid", "Valid", "text-emerald-500", "bg-emerald-500/10 border-emerald-500/20"],
  ["catch_all", "Catch-All", "text-sky-400", "bg-sky-500/10 border-sky-500/20"],
  ["risky", "Risky", "text-amber-500", "bg-amber-500/10 border-amber-500/20"],
  ["unknown", "Unknown", "text-slate-400", "bg-slate-500/10 border-slate-500/20"],
  ["invalid", "Invalid", "text-red-500", "bg-red-500/10 border-red-500/20"],
  ["verification_failed", "Failed", "text-red-400", "bg-red-500/10 border-red-500/20"],
  ["unverified", "Unverified", "text-orange-400", "bg-orange-500/10 border-orange-500/20"],
  ["verifying", "Verifying", "text-blue-400", "bg-blue-500/10 border-blue-500/20"],
];

const JOB_STATUS_COLORS = {
  queued: "bg-slate-500/15 text-slate-400", starting: "bg-blue-500/15 text-blue-400",
  uploading: "bg-blue-500/15 text-blue-400", verifying: "bg-blue-500/15 text-blue-400",
  verifying_single: "bg-blue-500/15 text-blue-400",
  completed: "bg-emerald-500/15 text-emerald-500", failed: "bg-red-500/15 text-red-400",
};

const SOURCE_LABELS = {
  csv_import: "CSV import", manual_select: "Manual selection",
  database_cleanup: "Database cleanup", campaign_precheck: "Campaign pre-check",
};

export default function VerificationTab({ onChanged }) {
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scope, setScope] = useState("needed");
  const [confirmCleanup, setConfirmCleanup] = useState(false);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncReport, setSyncReport] = useState(null);
  const pollRef = useRef(null);

  const fetchDash = useCallback(async () => {
    try {
      const res = await axios.get(`${MKT}/verification/dashboard`, getAdminAuth());
      setDash(res.data);
      return res.data;
    } catch (e) { toast.error("Failed to load verification dashboard"); return null; }
  }, []);

  useEffect(() => { fetchDash(); }, [fetchDash]);

  // Poll while jobs are active
  useEffect(() => {
    clearTimeout(pollRef.current);
    if (dash?.active_jobs > 0) {
      pollRef.current = setTimeout(async () => { await fetchDash(); onChanged?.(); }, 5000);
    }
    return () => clearTimeout(pollRef.current);
  }, [dash, fetchDash, onChanged]);

  const runCleanup = async () => {
    setCleanupBusy(true);
    try {
      const res = await axios.post(`${MKT}/verification/verify-database`, { scope }, getAdminAuth());
      toast.success(res.data.queued
        ? `${res.data.queued} email(s) queued for verification — runs in the background`
        : "Nothing to verify — database is already clean");
      setConfirmCleanup(false);
      fetchDash();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start cleanup"); }
    setCleanupBusy(false);
  };

  const runSync = async () => {
    setSyncBusy(true);
    setSyncReport(null);
    try {
      const res = await axios.post(`${MKT}/verification/sync-suppressions`, {}, getAdminAuth());
      setSyncReport(res.data);
      toast.success(`Sync complete — ${res.data.new_suppressions} new suppression(s) imported`);
      fetchDash(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Suppression sync failed"); }
    setSyncBusy(false);
  };

  const refresh = async () => { setLoading(true); await fetchDash(); setLoading(false); };

  if (!dash) return <p className="text-sm text-muted-foreground mt-4">Loading…</p>;

  const bs = dash.by_status || {};
  const bounce = dash.bounce_health || {};
  const bounceOver = bounce.threshold > 0 && bounce.rate > bounce.threshold;
  const needCleanup = (bs.unverified || 0) + (bs.verification_failed || 0) + (dash.expired || 0);

  return (
    <div className="space-y-4 mt-4" data-testid="verification-tab">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Database health powered by MailerSend Email Verification. Only verified addresses can receive campaigns — transactional email is never affected.
        </p>
        <Button variant="outline" size="sm" onClick={refresh} disabled={loading} data-testid="refresh-verification-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />Refresh
        </Button>
      </div>

      {/* Headline cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-border"><CardContent className="p-4">
          <p className="text-2xl font-bold text-foreground tabular-nums" data-testid="stat-total-contacts">{dash.total_contacts}</p>
          <p className="text-[11px] text-muted-foreground">Total contacts</p>
        </CardContent></Card>
        <Card className="border-emerald-500/30 bg-emerald-500/5"><CardContent className="p-4">
          <p className="text-2xl font-bold text-emerald-500 tabular-nums" data-testid="stat-eligible">{dash.eligible}</p>
          <p className="text-[11px] text-muted-foreground">Campaign-eligible (verified & not suppressed)</p>
        </CardContent></Card>
        <Card className="border-border"><CardContent className="p-4">
          <p className="text-2xl font-bold text-red-400 tabular-nums" data-testid="stat-suppressed">{dash.suppressions?.total || 0}</p>
          <p className="text-[11px] text-muted-foreground">Suppressed ({dash.suppressions?.by_status?.hard_bounce || 0} hard bounces)</p>
        </CardContent></Card>
        <Card className={`border ${bounceOver ? "border-red-500/40 bg-red-500/5" : "border-border"}`}><CardContent className="p-4">
          <p className={`text-2xl font-bold tabular-nums ${bounceOver ? "text-red-500" : "text-foreground"}`} data-testid="stat-bounce-rate">{bounce.rate}%</p>
          <p className="text-[11px] text-muted-foreground">Hard-bounce rate ({bounce.hard_bounces}/{bounce.sent} sent) · auto-pause at {bounce.threshold}%</p>
        </CardContent></Card>
      </div>

      {/* Status breakdown */}
      <Card className="border-border"><CardContent className="p-4">
        <p className="text-sm font-semibold text-foreground mb-3">Verification status breakdown</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="status-breakdown">
          {STATUS_META.map(([key, label, color, bg]) => (
            <div key={key} className={`rounded border p-2.5 text-center ${bg}`} data-testid={`status-card-${key}`}>
              <p className={`text-lg font-bold tabular-nums ${color}`}>{bs[key] || 0}</p>
              <p className="text-[10px] text-muted-foreground">{label}</p>
            </div>
          ))}
        </div>
        {dash.expired > 0 && (
          <p className="text-[11px] text-orange-400 mt-2" data-testid="expired-note">
            {dash.expired} verified result(s) are older than {dash.settings?.verification_expiry_days} days and need re-verification before sending.
          </p>
        )}
      </CardContent></Card>

      {/* Tools */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="border-border"><CardContent className="p-4 space-y-3">
          <p className="text-sm font-semibold text-foreground flex items-center gap-2">
            <DatabaseZap className="w-4 h-4 text-blue-400" /> Verify Existing Database
          </p>
          <p className="text-xs text-muted-foreground">
            Sends unverified addresses to MailerSend in the background. Suppressed and archived contacts are never verified (no wasted credits).
          </p>
          <div className="flex gap-2 items-center">
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="w-full sm:w-64" data-testid="cleanup-scope-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="needed">Needs verification ({needCleanup} contacts)</SelectItem>
                <SelectItem value="revalidate_all">Re-verify everything (uses more credits)</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" onClick={() => setConfirmCleanup(true)} disabled={cleanupBusy} data-testid="verify-database-btn">
              <ShieldCheck className="w-4 h-4 mr-1" />Verify Database
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground">Each address costs one MailerSend verification credit. Unverified / failed / expired results are re-checked; valid results are kept.</p>
        </CardContent></Card>

        <Card className="border-border"><CardContent className="p-4 space-y-3">
          <p className="text-sm font-semibold text-foreground flex items-center gap-2">
            <CloudDownload className="w-4 h-4 text-blue-400" /> Sync MailerSend Suppressions
          </p>
          <p className="text-xs text-muted-foreground">
            Pulls hard bounces, spam complaints, unsubscribes and blocklist entries from MailerSend into the local suppression list. Strictly additive — never removes local suppressions.
          </p>
          <Button size="sm" variant="outline" onClick={runSync} disabled={syncBusy} data-testid="sync-suppressions-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${syncBusy ? "animate-spin" : ""}`} />{syncBusy ? "Syncing…" : "Sync Now"}
          </Button>
          {syncReport && (
            <div className="text-[11px] text-muted-foreground space-y-0.5" data-testid="sync-report">
              <p><b className="text-foreground">{syncReport.new_suppressions}</b> new suppressions · {syncReport.already_local} already local</p>
              <p>Hard bounces: {syncReport.hard_bounces ?? 0} · Complaints: {syncReport.spam_complaints ?? 0} · Unsubscribes: {syncReport.unsubscribes ?? 0} · Blocklist: {syncReport.blocklist ?? 0} · On hold: {syncReport.on_hold_list ?? 0}</p>
              {Object.keys(syncReport.errors || {}).length > 0 && (
                <p className="text-red-400 flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{Object.entries(syncReport.errors).map(([k, v]) => `${k}: ${v}`).join(" · ")}</p>
              )}
            </div>
          )}
        </CardContent></Card>
      </div>

      {/* Jobs */}
      <Card className="border-border"><CardContent className="p-0">
        <div className="p-4 pb-2 flex items-center gap-2">
          <p className="text-sm font-semibold text-foreground">Verification jobs</p>
          {dash.active_jobs > 0 && <Badge className="bg-blue-500/15 text-blue-400 text-[10px] animate-pulse">{dash.active_jobs} running</Badge>}
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="verification-jobs-table">
          <thead><tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
            <th className="p-3">Source</th><th className="p-3">Status</th><th className="p-3 w-56">Progress</th>
            <th className="p-3">Results</th><th className="p-3">Started</th>
          </tr></thead>
          <tbody>
            {(dash.recent_jobs || []).map(j => {
              const pct = j.total ? Math.round(Math.min(j.verified_count || 0, j.total) / j.total * 100) : 0;
              return (
                <tr key={j.job_id} className="border-b border-border/40" data-testid={`job-row-${j.job_id}`}>
                  <td className="p-3 text-xs">{SOURCE_LABELS[j.source] || j.source}</td>
                  <td className="p-3"><Badge className={`text-[10px] ${JOB_STATUS_COLORS[j.status] || "bg-slate-500/15 text-slate-400"}`}>{j.status.replace("_single", "")}</Badge></td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${j.status === "failed" ? "bg-red-500" : j.status === "completed" ? "bg-emerald-500" : "bg-blue-500"}`}
                          style={{ width: `${j.status === "completed" ? 100 : pct}%` }} />
                      </div>
                      <span className="text-[10px] text-muted-foreground tabular-nums whitespace-nowrap">{j.verified_count || 0}/{j.total}</span>
                    </div>
                    {j.error && <p className="text-[10px] text-red-400 mt-1">{j.error}</p>}
                  </td>
                  <td className="p-3 text-[10px] text-muted-foreground">
                    {Object.keys(j.counts || {}).length
                      ? Object.entries(j.counts).map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`).join(" · ")
                      : "—"}
                  </td>
                  <td className="p-3 text-[10px] text-muted-foreground whitespace-nowrap">{j.created_at ? new Date(j.created_at).toLocaleString() : "—"}</td>
                </tr>
              );
            })}
            {(dash.recent_jobs || []).length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-muted-foreground text-sm">No verification jobs yet — import a CSV or run a database cleanup.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </CardContent></Card>

      {/* Cleanup confirm */}
      <AlertDialog open={confirmCleanup} onOpenChange={setConfirmCleanup}>
        <AlertDialogContent data-testid="cleanup-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Start database verification?</AlertDialogTitle>
            <AlertDialogDescription>
              {scope === "revalidate_all"
                ? `This re-verifies EVERY non-suppressed contact (${dash.total_contacts} max) — one MailerSend credit each.`
                : `This verifies the ${needCleanup} contact(s) that are unverified, failed, or expired — one MailerSend credit each.`}
              {" "}Verification runs in the background; progress appears in the jobs table below.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-cleanup">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runCleanup} disabled={cleanupBusy} data-testid="confirm-cleanup">
              Start Verification
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
